/**
 * Centralized Configuration
 *
 * This file contains all application configuration derived from environment variables.
 * Use this instead of directly accessing process.env throughout the application.
 */

// Helper to parse boolean environment variables
const parseBoolean = (value: string | undefined, defaultValue: boolean = false): boolean => {
  if (!value) return defaultValue;
  return value.toLowerCase() === 'true' || value === '1';
};

// Helper to parse integer environment variables
const parseInt = (value: string | undefined, defaultValue: number): number => {
  if (!value) return defaultValue;
  const parsed = Number.parseInt(value, 10);
  return isNaN(parsed) ? defaultValue : parsed;
};

/**
 * Application Configuration
 */
export const config = {
  /**
   * Application Settings
   */
  app: {
    name: process.env.NEXT_PUBLIC_APP_NAME || 'JobWright',
    url: process.env.NEXT_PUBLIC_APP_URL || 'http://localhost:3000',
    env: process.env.NODE_ENV || 'development',
    isDevelopment: process.env.NODE_ENV === 'development',
    isProduction: process.env.NODE_ENV === 'production',
    isTest: process.env.NODE_ENV === 'test',
  },

  /**
   * Feature Flags
   */
  features: {
    enableGPS: parseBoolean(process.env.NEXT_PUBLIC_ENABLE_GPS, true),
    enableSMS: parseBoolean(process.env.NEXT_PUBLIC_ENABLE_SMS, true),
    enablePortal: parseBoolean(process.env.NEXT_PUBLIC_ENABLE_PORTAL, true),
    enableAutomations: parseBoolean(process.env.NEXT_PUBLIC_ENABLE_AUTOMATIONS, true),
    enableTimeTracking: parseBoolean(process.env.NEXT_PUBLIC_ENABLE_TIME_TRACKING, true),
    enableRecurringJobs: parseBoolean(process.env.NEXT_PUBLIC_ENABLE_RECURRING_JOBS, true),
    enablePriceBook: parseBoolean(process.env.NEXT_PUBLIC_ENABLE_PRICE_BOOK, true),
    enableDispatch: parseBoolean(process.env.NEXT_PUBLIC_ENABLE_DISPATCH, true),
  },

  /**
   * API Configuration
   */
  api: {
    timeout: parseInt(process.env.API_TIMEOUT, 30000), // 30 seconds
    retries: parseInt(process.env.API_RETRIES, 3),
    retryDelay: parseInt(process.env.API_RETRY_DELAY, 1000), // 1 second
  },

  /**
   * File Upload Limits
   */
  uploads: {
    maxFileSize: parseInt(process.env.MAX_FILE_SIZE, 5 * 1024 * 1024), // 5MB
    maxPhotoSize: parseInt(process.env.MAX_PHOTO_SIZE, 10 * 1024 * 1024), // 10MB
    maxLogoSize: parseInt(process.env.MAX_LOGO_SIZE, 5 * 1024 * 1024), // 5MB
    allowedImageTypes: ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'],
    allowedLogoTypes: ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/svg+xml'],
  },

  /**
   * Business Limits
   */
  limits: {
    maxTeamMembers: parseInt(process.env.MAX_TEAM_MEMBERS, 50),
    maxClients: parseInt(process.env.MAX_CLIENTS, 1000),
    maxJobs: parseInt(process.env.MAX_JOBS, 10000),
    maxAutomations: parseInt(process.env.MAX_AUTOMATIONS, 50),
  },

  /**
   * Polling & Refresh Intervals (in milliseconds)
   */
  intervals: {
    notifications: parseInt(process.env.INTERVAL_NOTIFICATIONS, 30000), // 30 seconds
    conversations: parseInt(process.env.INTERVAL_CONVERSATIONS, 10000), // 10 seconds
    teamLocations: parseInt(process.env.INTERVAL_TEAM_LOCATIONS, 30000), // 30 seconds
    gpsTracking: parseInt(process.env.INTERVAL_GPS_TRACKING, 10000), // 10 seconds
    autoSave: parseInt(process.env.INTERVAL_AUTO_SAVE, 3000), // 3 seconds
  },

  /**
   * Branding Defaults
   */
  branding: {
    defaultPrimaryColor: process.env.DEFAULT_PRIMARY_COLOR || '#3b82f6', // blue-500
    defaultSecondaryColor: process.env.DEFAULT_SECONDARY_COLOR || '#8b5cf6', // violet-500
    defaultTextOnDark: process.env.DEFAULT_TEXT_ON_DARK || '#ffffff',
    defaultTextOnLight: process.env.DEFAULT_TEXT_ON_LIGHT || '#000000',
    defaultSpinnerColor: process.env.DEFAULT_SPINNER_COLOR || '#64748b', // slate-500
  },

  /**
   * Security Settings
   */
  security: {
    hstsMaxAge: parseInt(process.env.HSTS_MAX_AGE, 31536000), // 1 year
    sessionMaxAge: parseInt(process.env.SESSION_MAX_AGE, 30 * 24 * 60 * 60), // 30 days
    portalTokenExpiry: parseInt(process.env.PORTAL_TOKEN_EXPIRY, 24 * 60 * 60), // 24 hours
    csrfTokenExpiry: parseInt(process.env.CSRF_TOKEN_EXPIRY, 24 * 60 * 60), // 24 hours
    enableCSP: parseBoolean(process.env.ENABLE_CSP, true),
    enableHSTS: parseBoolean(process.env.ENABLE_HSTS, true),
  },

  /**
   * Tax Settings
   */
  tax: {
    defaultTaxRate: parseFloat(process.env.DEFAULT_TAX_RATE || '0.08'), // 8%
    enableTax: parseBoolean(process.env.ENABLE_TAX, true),
  },

  /**
   * External Service URLs
   */
  services: {
    stripe: {
      checkoutUrl: 'https://js.stripe.com',
      hooksUrl: 'https://hooks.stripe.com',
    },
    google: {
      mapsApiUrl: 'https://maps.googleapis.com',
      mapsSearchUrl: 'https://www.google.com/maps/search/',
      fontsUrl: 'https://fonts.googleapis.com',
      fontStaticUrl: 'https://fonts.gstatic.com',
      apiUrl: 'https://*.googleapis.com',
    },
    telnyx: {
      apiUrl: 'https://api.telnyx.com/v2',
      portalUrl: 'https://portal.telnyx.com/#/app/messaging',
    },
    schema: {
      contextUrl: 'https://schema.org',
    },
  },

  /**
   * Marketing & SEO
   */
  marketing: {
    companyName: process.env.NEXT_PUBLIC_COMPANY_NAME || 'JobWright',
    companyDomain: process.env.NEXT_PUBLIC_COMPANY_DOMAIN || 'jobwright.com',
    companyWwwDomain: process.env.NEXT_PUBLIC_COMPANY_WWW_DOMAIN || 'www.jobwright.com',
    supportEmail: process.env.SUPPORT_EMAIL || 'support@jobwright.com',
    salesEmail: process.env.SALES_EMAIL || 'sales@jobwright.com',
    socialMedia: {
      twitter: process.env.SOCIAL_TWITTER || 'https://twitter.com/jobwright',
      facebook: process.env.SOCIAL_FACEBOOK || 'https://facebook.com/jobwright',
      linkedin: process.env.SOCIAL_LINKEDIN || 'https://linkedin.com/company/jobwright',
      instagram: process.env.SOCIAL_INSTAGRAM || 'https://instagram.com/jobwright',
      youtube: process.env.SOCIAL_YOUTUBE || 'https://youtube.com/@jobwright',
      github: process.env.SOCIAL_GITHUB || 'https://github.com/jobwright',
    },
  },

  /**
   * Logging Configuration
   */
  logging: {
    level: process.env.LOG_LEVEL || (process.env.NODE_ENV === 'development' ? 'debug' : 'info'),
    enableConsole: parseBoolean(process.env.ENABLE_CONSOLE_LOGS, true),
    enableFile: parseBoolean(process.env.ENABLE_FILE_LOGS, false),
  },

  /**
   * Database Configuration
   */
  database: {
    url: process.env.DATABASE_URL,
    enableLogging: parseBoolean(process.env.DATABASE_LOGGING, process.env.NODE_ENV === 'development'),
  },

  /**
   * Authentication Configuration
   */
  auth: {
    secret: process.env.NEXTAUTH_SECRET || process.env.AUTH_SECRET,
    url: process.env.NEXTAUTH_URL || process.env.NEXT_PUBLIC_APP_URL || 'http://localhost:3000',
    debug: parseBoolean(process.env.AUTH_DEBUG, process.env.NODE_ENV === 'development'),
  },

  /**
   * Email Configuration
   */
  email: {
    from: process.env.EMAIL_FROM || 'JobWright <noreply@jobwright.com>',
    replyTo: process.env.EMAIL_REPLY_TO,
    enableEmails: parseBoolean(process.env.ENABLE_EMAILS, true),
    provider: process.env.EMAIL_PROVIDER || 'resend',
  },

  /**
   * Payment Configuration
   */
  payment: {
    provider: process.env.PAYMENT_PROVIDER || 'stripe',
    currency: process.env.PAYMENT_CURRENCY || 'USD',
    locale: process.env.PAYMENT_LOCALE || 'en-US',
  },

  /**
   * Geolocation Configuration
   */
  geolocation: {
    timeout: parseInt(process.env.GEO_TIMEOUT, 10000), // 10 seconds
    maxAge: parseInt(process.env.GEO_MAX_AGE, 10000), // 10 seconds
    enableHighAccuracy: parseBoolean(process.env.GEO_HIGH_ACCURACY, true),
  },

  /**
   * Cron Configuration
   */
  cron: {
    secret: process.env.CRON_SECRET,
    enableReminders: parseBoolean(process.env.ENABLE_CRON_REMINDERS, true),
  },
} as const;

/**
 * Type-safe environment variable checker
 */
export function checkRequiredEnvVars(): { missing: string[]; warnings: string[] } {
  const missing: string[] = [];
  const warnings: string[] = [];

  // Required for production
  if (config.app.isProduction) {
    if (!process.env.DATABASE_URL) missing.push('DATABASE_URL');
    if (!config.auth.secret) missing.push('NEXTAUTH_SECRET or AUTH_SECRET');
    if (!process.env.NEXT_PUBLIC_APP_URL) warnings.push('NEXT_PUBLIC_APP_URL');
  }

  // Required for features
  if (config.features.enableSMS && !process.env.TELNYX_API_KEY) {
    warnings.push('TELNYX_API_KEY (required for SMS)');
  }

  if (!process.env.RESEND_API_KEY && config.email.enableEmails) {
    warnings.push('RESEND_API_KEY (required for emails)');
  }

  if (config.features.enableDispatch && !process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY) {
    warnings.push('NEXT_PUBLIC_GOOGLE_MAPS_API_KEY (required for dispatch/maps)');
  }

  if (!process.env.STRIPE_SECRET_KEY) {
    warnings.push('STRIPE_SECRET_KEY (required for payments)');
  }

  return { missing, warnings };
}

/**
 * Helper to get absolute URL
 */
export function getAbsoluteUrl(path: string = ''): string {
  const baseUrl = config.app.url.replace(/\/$/, ''); // Remove trailing slash
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${baseUrl}${normalizedPath}`;
}

/**
 * Helper to format currency
 */
export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat(config.payment.locale, {
    style: 'currency',
    currency: config.payment.currency,
  }).format(amount);
}

/**
 * Export for backwards compatibility
 */
export default config;
